import { v4 as uuidv4 } from 'uuid';
import { chunk, isEmpty, isUndefined, last } from 'lodash';
import { PartialDeep } from 'type-fest';
import dayjs from '~/common/dayjs';
import { ContainerBlock } from '~/models/blocks/container-block.model';
import { TextBlock } from '~/models/blocks/text-block.model';
import { ImageBlock } from '~/models/blocks/image-block.model';
import {
    BlockDndDirection,
    CommandType,
    CompanyInfoLayoutType,
    ICreateBlockContext,
    IDuplicateBlockContext,
    IMoveBlockContext,
    TBlock,
    TBlockCreationDirection,
    TBlockDataForServer,
    TCommand
} from '~/types/builder.types';
import {
    IBaseBlockResponse,
    IContainerableBlockResponse,
    IMember,
    IPaymentRecap,
    TBlockResponse,
    IContractDropdownField
} from '~/types/server.types';
import {
    AmountCalcType,
    CompositionType,
    DateCalcType,
    DiscountTypes,
    IChildLayout,
    IImage,
    IInvoiceSummary,
    ServiceItemType,
    ServicesItemsLayoutType,
    SmartDatePeriodRelation,
    SmartDatePeriodType,
    TaxType,
    TBlockType
} from '~/types/model.types';

import type { RootStore } from '~/stores/root.store';
import { ContentWidth } from '~/types/block-styles.types';
import {
    QuestionBlock,
    QuestionBlockDataForServer
} from '~/models/blocks/question-block.model';
import { VideoBlock } from '~/models/blocks/video-block.model';
import { ICompositionLayoutStruct } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.types';
import { SpacerBlock } from '~/models/blocks/spacer-block.model';
import { ContractBlock } from '~/models/blocks/contract-block.model';
import { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import { BaseBlock } from '~/models/blocks/base-block.model';
import { SignaturesBlock } from '~/models/blocks/signatures-block.model';
import { SignatureBlock } from '~/models/blocks/signature-block.model';
import { ContractHTMLBlock } from '~/models/blocks/contract-html-block.model';
import { ServicesBlock } from '~/models/blocks/services-block.model';
import { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import { PaymentBlock } from '~/models/blocks/payment-block.model';
import { InvoiceHeaderBlock } from '~/models/blocks/invoice-header-block.model';
import { SocialBlock } from '~/models/blocks/social-block.model';
import { InvoiceSummaryBlock } from '~/models/blocks/invoice-summary-block.model';
import {
    Action,
    IBasicCommandsActionValue
} from '~/services/actions-manager/action';
import {
    createCreateBlockCommand,
    createDeleteBlockCommand,
    createUpdateBlockCommand
} from '~/utils/commands.utils';
import { paddingYMap } from '~/components/common/HorizontalContainer/HorizontalContainer.styles';
import { ChargeBlock } from '~/models/blocks/charge-block.model';
import { ChargeContainerBlock } from '~/models/blocks/charge-container-block.model';
import { Flow } from '~/models/flow.model';
import { BlockController } from '~/controllers/block.controller';
import { Company } from '~/models/company.model';
import {
    DEFAULT_COMPANY_INFO_LAYOUT,
    getCompanyInfoLayouts
} from '~/components/Builder/BuilderSidebar/ContainerSidebar/company-info-layout.utils';
import { GenerateSmartFieldsID } from '~/components/Builder/BuilderSidebar/ContractSidebar/ContractHtml.utils';
import { InvoiceColumnHeaderBlock } from '~/models/blocks/invoice-column-header-block.model';
import { BLANK_CONTRACT_OBJECT } from '~/utils/tempContract';
import { FlowRevision } from '~/models/flow-revision.model';
import { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import { ServiceItemsContainerBlock } from '~/models/blocks/service-items-container-block.model';
import { itemsPerContainer } from '~/components/Builder/Block/ServiceSelectionBlock/service-selection-block.utils';
import { InvoiceServiceItemBlock } from '~/models/blocks/invoice-service-item-block.model';
import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import { RecurringScheduleBlock } from '~/models/blocks/recurring-schedule-block.model';
import { CompanyInfoBlock } from '~/models/blocks/company-info-block.model';
import {
    BASE_ACTION_BLOCK_TYPES,
    BASE_BLOCK_TYPES
} from '~/common/flows.constants';
import { CXCompanyInfoBlock } from '~/components/ClientExperience/OneFlow/models/cx-company-info-block.model';
import { CXImageBlock } from '~/components/ClientExperience/OneFlow/models/cx-image-block.model';
import { CXTextBlock } from '~/components/ClientExperience/OneFlow/models/cx-text-block.model';
import { CXContainerableBlock } from '~/components/ClientExperience/OneFlow/models/cx-containerable-block.model';
import {
    IErrorAdditionalProps,
    PaymentScheduleBlockErrors
} from '~/types/error.types';
import { falseNotZero } from '~/utils/lodash.utils';
import { toCurrency } from '@honeybook/hbui/dist/components/HBCurrency/HBCurrency';
import { ScheduleContainerBlock } from '~/models/blocks/schedule-container-block.model';
import { ScheduleSessionBlock } from '~/models/blocks/schedule-session-block.model';
import { DAY_NAMES, formatDate, MONTH_NAMES } from '~/utils/date.utils';
import { NAMESPACES } from '~/i18n/namespaces';
import { getFixedT } from 'i18next';
import { LeadDetailsBlock } from '~/models/blocks/lead-details-block.model';
import {
    GetInitialBlockStyles,
    INITIAL_BLOCK_STYLES
} from '~/utils/initial-block-styles';
import { formatFirstPaymentDueDate } from '~/components/common/InvoiceBlock/invoice.utils';
import { isPublicTemplateViewMode } from '~/utils/public-template-view-mode-flow.utils';
import { CXFlow } from '~/components/ClientExperience/OneFlow/models/cx-flow.model';
import { IframeBlock } from '~/models/blocks/iframe-block.model';

const TOTAL_EXTRA_PADDING_SIZE = 60;

export const ACTION_TYPE_TO_SOURCE_TYPE_MAP = {
    CreateBlock: 'manual',
    CreateCompositionBlock: 'manual',
    CreateInvoiceBlock: 'manual',
    CreatePage: 'manual',
    CreateBlankInvoice: 'manual',
    DuplicateBlock: 'duplicate',
    DuplicatePage: 'duplicate',
    ImportBlock: 'block_reused',
    ImportPages: 'page_reused',
    PasteBlock: 'paste'
};

export enum Screens {
    XLARGE = 'xl',
    LARGE = 'lg',
    MEDIUM = 'md',
    SMALL = 'sm'
}

export function getContentWidth(
    contentWidth: ContentWidth,
    screenSize: Screens,
    canvasWidth: number
) {
    if (contentWidth === ContentWidth.FULL) {
        return canvasWidth;
    }

    if (contentWidth === ContentWidth.LARGE && screenSize === Screens.XLARGE) {
        return 1140;
    }

    if (
        [ContentWidth.LARGE, ContentWidth.MEDIUM].includes(contentWidth) &&
        [Screens.XLARGE, Screens.LARGE].includes(screenSize)
    ) {
        return 800;
    }

    return 420;
}

export function getInvoiceSummaryDefaultsForClientPaymentPlan(): Partial<IInvoiceSummary> {
    return {
        is_client_payment_plan: false,
        max_number_of_payments: 12,
        first_payment_due_date_type: DateCalcType.DateSent,
        first_payment_due_date: formatFirstPaymentDueDate(new Date()),
        allow_pay_in_full_discount: false,
        pay_in_full_discount: 10,
        pay_in_full_discount_type: DiscountTypes.Relative
    };
}

export const INITIAL_BLOCK_ATTRIBUTES: {
    invoice_service_item_block: PartialDeep<InvoiceServiceItemBlock>;
    service_selection_item_block: PartialDeep<ServiceSelectionItemBlock>;
    invoice_summary_block: Partial<InvoiceSummaryBlock>;
    invoice_header_block: Partial<InvoiceHeaderBlock>;
    service_selection_block: Partial<ServiceSelectionBlock>;
    schedule_container_block: Partial<ScheduleContainerBlock>;
    payment_block: Partial<PaymentBlock>;
} = {
    invoice_service_item_block: {
        service_type: ServiceItemType.Service,
        service_item: {}
    },
    service_selection_item_block: {
        service_type: ServiceItemType.Service,
        service_item: {
            quantity: 1
        },
        max_quantity: Infinity,
        min_quantity: 1
    },
    invoice_header_block: {
        invoice_header: {
            title: 'INVOICE',
            bill_from_content: null,
            project_details: null
        }
    },
    invoice_summary_block: {
        invoice_summary: {
            discount: 0,
            discount_type: null,
            tax_1: {
                label: 'Tax',
                value: 8.75,
                type: TaxType.Relative,
                tax_id: uuidv4(),
                active: true
            },
            tax_2: {
                label: 'SVC',
                value: 0,
                type: TaxType.Relative,
                tax_id: uuidv4(),
                active: false
            },
            tax_3: {
                label: 'Tax 3',
                value: 0,
                type: TaxType.Relative,
                tax_id: uuidv4(),
                active: false
            },
            late_fee_enabled: false,
            late_fee_percentage: 1.5
        }
    },
    // TODO: is this leftover? payment schedule block doesn't
    // have a payment_method_types field
    // @ts-ignore
    payment_schedule_block: {
        payment_method_types: ['bank_account', 'credit_card']
    },
    recurring_schedule_block: {
        recurring_schedule: getRecurringScheduleDefault(),
        upcoming_invoices: []
    },
    schedule_container_block: {
        is_required: true,
        schedule_session_trigger: 'immediately'
    },
    payment_block: {
        payment_content: {}
    },
    charge_block: {
        allow_gratuity: true,
        gratuity_type: 'all_payments'
    },
    service_selection_block: {
        service_selection_type: 'service'
    }
};

export function getRecurringScheduleDefault() {
    const today = new Date();
    const day_of_month = today.getDate();
    const day_of_week = DAY_NAMES[today.getDay()];
    const month_of_year = MONTH_NAMES[today.getMonth()];
    return {
        custom_unit_count: 1,
        custom_unit_type: 'months',
        day_of_month,
        day_of_week,
        end_date: null,
        end_type: 'never',
        first_invoice_due_date: formatDate(today, 'YYYY-MM-dd'),
        month_of_year,
        repetition_count: 20,
        repetition_unit_type: 'monthly'
    };
}

export const BLOCK_CLASS_BY_TYPE = {
    charge_block: ChargeBlock,
    charge_container_block: ChargeContainerBlock,
    company_info_block: CompanyInfoBlock,
    container_block: ContainerBlock,
    contract_block: ContractBlock,
    contract_html_block: ContractHTMLBlock,
    image_block: ImageBlock,
    iframe_block: IframeBlock,
    invoice_container_block: InvoiceBlock,
    invoice_header_block: InvoiceHeaderBlock,
    invoice_column_header_block: InvoiceColumnHeaderBlock,
    invoice_service_item_block: InvoiceServiceItemBlock,
    invoice_summary_block: InvoiceSummaryBlock,
    lead_details_block: LeadDetailsBlock,
    payment_block: PaymentBlock,
    payment_schedule_block: PaymentScheduleBlock,
    question_block: QuestionBlock,
    recurring_schedule_block: RecurringScheduleBlock,
    schedule_container_block: ScheduleContainerBlock,
    schedule_session_block: ScheduleSessionBlock,
    service_items_container_block: ServiceItemsContainerBlock,
    service_selection_block: ServiceSelectionBlock,
    service_selection_item_block: ServiceSelectionItemBlock,
    services_block: ServicesBlock,
    signature_block: SignatureBlock,
    signatures_block: SignaturesBlock,
    social_block: SocialBlock,
    spacer_block: SpacerBlock,
    text_block: TextBlock,
    video_block: VideoBlock
} satisfies Record<TBlockType, typeof BaseBlock>;

export function createBlockInstance<T extends TBlock>(
    block: Omit<IBaseBlockResponse, 'parent_id'>,
    flowRevision: FlowRevision<RootStore>,
    rootStore: RootStore,
    initialAttributes?: any
): T {
    const BlockClass = BLOCK_CLASS_BY_TYPE[block.type];

    if (!BlockClass) {
        throw new Error('Block should have type');
    }

    return <T>(
        new BlockClass(block as any, flowRevision, rootStore, initialAttributes)
    );
}

export function getNewBlockOrder(
    block: Pick<TBlock, 'parent' | 'block_id'>,
    direction: TBlockCreationDirection,
    isParent: boolean = false
) {
    const parent = isParent ? (block as ContainerableBlock) : block.parent;

    if (!parent || !parent.children.length) {
        return 1;
    }

    const childLayouts = parent.sortedChildLayouts;

    function getBlockIndex() {
        if (!isParent) {
            return childLayouts.findIndex(
                item => item.block_id === block.block_id
            );
        }
        // if isParent, in case 'top' we want to add at the beginning,
        // if 'bottom' we want to add at the end
        return direction === 'top' ? 0 : childLayouts.length - 1;
    }

    const blockIndex = getBlockIndex();
    const prevOrNextBlockIndex = blockIndex + (direction === 'top' ? -1 : 1);

    if (!childLayouts[prevOrNextBlockIndex]) {
        return childLayouts[blockIndex].order + (direction === 'top' ? -1 : 1);
    }

    return (
        (childLayouts[blockIndex]?.order +
            childLayouts[prevOrNextBlockIndex]?.order) /
        2
    );
}

export function getOrderForMoveBlock(
    parentBlock: ContainerableBlock,
    direction: TBlockCreationDirection
) {
    const childLayouts = parentBlock.sortedChildLayouts;

    if (isEmpty(childLayouts)) {
        return 0;
    }

    return direction === BlockDndDirection.Top
        ? childLayouts[0].order - 1
        : childLayouts[childLayouts.length - 1].order + 1;
}

/**  this logic matches generate_bill_to_content in hpd, changes should be simultaneous */
export function getFormattedMemberBillingDetails(member: IMember): string {
    let result = '';
    result = member?.full_name ? `${result}${member?.full_name}` : result;

    const {
        name: organizationName,
        address: organizationAddress,
        phone_number: organizationPhoneNumber
    } = member?.client_organization || {};

    if (organizationName) {
        result = `${result}\n${organizationName}`;
    } else if (member?.member_company_info?.company_name) {
        result = `${result}\n${member.member_company_info.company_name}`;
    }

    const address = organizationAddress || member?.address;
    result = address ? `${result}\n${address}` : result;

    result = member?.email ? `${result}\n${member?.email}` : result;

    const phoneNumber = organizationPhoneNumber || member?.phone_number;
    result = phoneNumber ? `${result}\n${phoneNumber}` : result;

    return result;
}

export function doesContainLinkedQuestion(block: TBlock) {
    let duplicatedBlocks = [block];

    if ('allDescendants' in block) {
        duplicatedBlocks = duplicatedBlocks.concat([...block.allDescendants()]);
    }

    const linkedQuestion = duplicatedBlocks.find(
        blk => 'question' in blk && !!blk.question?.connected_property
    );

    return !!linkedQuestion;
}

// ----------------------------- CREATE BLOCK ---------------------- //
function createBlockAttributes(
    parent: ContainerableBlock,
    type: TBlockType,
    opts: Partial<TBlockDataForServer> = {},
    composition_type?: CompositionType
) {
    const getInitialStyles: GetInitialBlockStyles = INITIAL_BLOCK_STYLES[type];

    return {
        block_id: uuidv4(),
        type,
        composition_type,
        parent_id: parent?.block_id,
        page_id: parent?.page_id,
        flow_id: parent?.flow_id,
        flowRevision: parent?.flowRevision,
        styles: {
            ...(getInitialStyles?.(parent) || {})
        },
        ...(INITIAL_BLOCK_ATTRIBUTES[type] || {}),
        ...opts
    };
}

interface ICreateChildBlockProps {
    rootStore: RootStore;
    parent: ContainerableBlock;
    type: TBlockType;
    opts?: Partial<TBlockResponse>;
    compositionType?: CompositionType;
    order?: number;
    size?: number;
}

// creates a new block and assigns it as the child of the parent block
function createChildBlock<T extends TBlock>({
    rootStore,
    parent,
    type,
    opts,
    compositionType,
    order,
    size
}: ICreateChildBlockProps): T {
    const block = createBlockInstance<T>(
        createBlockAttributes(parent, type, opts, compositionType),
        parent.flowRevision,
        rootStore
    );
    parent.addChild(block, order, size);

    return block as T;
}

function getTextBlockContent(
    struct: ICompositionLayoutStruct,
    isLeadForm: boolean
) {
    const t = getFixedT(null, NAMESPACES.BUILDER, 'text_block');

    const typeText = isLeadForm
        ? t('placeholder')
        : t('placeholder_smart_fields');

    if (struct.type === 'text_block') {
        if (struct.text_content && !struct.text_style) {
            return struct.text_content;
        }
        if (struct.text_style && struct.text_style.type) {
            if (struct.text_style.type === 'regular') {
                return `<p style="text-align:${
                    struct.text_style.alignment
                };"><span style="color:${
                    struct.text_style.color
                };font-size:18px;">${
                    struct.text_content || 'This is some text. Type something'
                }</span></p>`;
            }
            if (struct.text_style.type === 'title') {
                return `<p style="text-align:${
                    struct.text_style.alignment
                };"><span style="color:${
                    struct.text_style.color
                };font-size:16px;"><strong>${
                    struct.text_content || `This is a title. ${typeText}`
                }</strong></span></p>`;
            }
            if (struct.text_style.type === 'short_title') {
                return `<p style="text-align:${
                    struct.text_style.alignment
                };"><span style="color:${
                    struct.text_style.color
                };font-size:16px;"><strong>${
                    struct.text_content || 'This is a title'
                }</strong></span></p>`;
            }
            if (struct.text_style.type === 'description') {
                return `<p style="text-align:${
                    struct.text_style.alignment
                };"><span style="color:${struct.text_style.color};">${
                    struct.text_content ||
                    'This is a text paragraph. Click here to type your text and highlight to apply your style.'
                }</span></p>`;
            }
            if (struct.text_style.type === 'two_rows_description') {
                return `<p style="text-align:${
                    struct.text_style.alignment
                };"><span style="color:${
                    struct.text_style.color
                };">This is a text paragraph.&nbsp;</span><br><span style="color:${
                    struct.text_style.color
                };">${
                    struct.text_content ||
                    'Click here to type your text and highlight to apply your style.'
                }</span></p>`;
            }
        }
    }

    return null;
}

function getLayoutBlockOptions(
    struct: ICompositionLayoutStruct,
    rootStore: RootStore
) {
    const {
        builderUiStore: {
            selectedFlow: { isLeadForm }
        }
    } = rootStore;

    const content = getTextBlockContent(struct, isLeadForm);

    const { text_content, text_style, type, children, ..._struct } = struct;

    return content ? { ..._struct, content } : _struct;
}

export interface ICreateChildBlockResult {
    newBlocksMap: Record<string, TBlock>;
    newTargetBlock: TBlock;
    newChildLayout: IChildLayout;
    commands?: TCommand[];
    initialAttributes?: any;
}

function createContractBlock(
    parent: ContainerBlock,
    type: 'contract_block',
    order: number,
    rootStore: RootStore
): ICreateChildBlockResult {
    const {
        builderUiStore: { selectedFlow, setShowContractOverlayForId }
    } = rootStore;

    const firstVendor = (selectedFlow && selectedFlow.firstFlowVendor) || {
        _id: '',
        full_name: '',
        email: ''
    };
    const firstClientParticipant = (selectedFlow &&
        selectedFlow.firstFlowClient) || {
        _id: '',
        full_name: '',
        email: ''
    };

    // This doesn't use createChildBlock because the adding of it happens
    // as part of the create commands
    const contractBlock = createBlockInstance<ContractBlock>(
        createBlockAttributes(parent, type),
        parent.flowRevision,
        rootStore
    );

    const contractHtmlBlock = createChildBlock<ContractHTMLBlock>({
        rootStore,
        parent: contractBlock,
        type: 'contract_html_block',
        opts: {
            contract: BLANK_CONTRACT_OBJECT
        },
        order: 1
    });

    const signaturesBlock = createChildBlock<SignaturesBlock>({
        rootStore,
        parent: contractBlock,
        type: 'signatures_block',
        order: 2
    });

    const vendorSignatureBlock = createChildBlock<SignatureBlock>({
        rootStore,
        parent: signaturesBlock,
        type: 'signature_block',
        opts: {
            signature: {
                signee_id: firstVendor._id,
                signee_name: firstVendor.full_name,
                signee_email: firstVendor.email,
                signee_type: 'vendor',
                required: true,
                signee_details: null
            }
        }
    });

    const clientSignatureBlock = createChildBlock<SignatureBlock>({
        rootStore,
        parent: signaturesBlock,
        type: 'signature_block',
        opts: {
            signature: {
                signee_id: firstClientParticipant._id,
                signee_name: firstClientParticipant.full_name,
                signee_email: firstClientParticipant.email,
                signee_type: 'client',
                required: true,
                signee_details: null
            }
        }
    });

    setShowContractOverlayForId(contractBlock.block_id);

    return {
        newBlocksMap: {
            [contractBlock.block_id]: contractBlock,
            [signaturesBlock.block_id]: signaturesBlock,
            [contractHtmlBlock.block_id]: contractHtmlBlock,
            [vendorSignatureBlock.block_id]: vendorSignatureBlock,
            [clientSignatureBlock.block_id]: clientSignatureBlock
        },
        newTargetBlock: contractBlock,
        newChildLayout: { block_id: contractBlock.block_id, order, size: 1 }
    };
}

function calcSecondPaymentOpts(projectDate) {
    const secondPaymentOpts: Record<string, any> = {};
    const projectDaysDiff =
        projectDate && dayjs(projectDate).diff(dayjs(), 'day');

    if (!projectDate || projectDaysDiff <= 0) {
        secondPaymentOpts.payment_content = {
            smart_date_period: 1,
            smart_date_period_relation: SmartDatePeriodRelation.AfterSent,
            smart_date_period_type: SmartDatePeriodType.Month,
            date_calc_type: DateCalcType.SmartDate,
            amount_calc_type: AmountCalcType.DivideEqually
        };
    } else if (projectDaysDiff < 30) {
        secondPaymentOpts.payment_content = {
            date_calc_type: DateCalcType.ProjectDate,
            amount_calc_type: AmountCalcType.DivideEqually
        };
    } else {
        secondPaymentOpts.payment_content = {
            smart_date_period: 1,
            smart_date_period_relation: SmartDatePeriodRelation.BeforeProject,
            smart_date_period_type: SmartDatePeriodType.Month,
            date_calc_type: DateCalcType.SmartDate,
            amount_calc_type: AmountCalcType.DivideEqually
        };
    }

    return secondPaymentOpts;
}

function createInvoiceBlock(
    parent: ContainerBlock,
    type: 'invoice_container_block',
    order: number,
    rootStore: RootStore,
    initialAttributes: any
): ICreateChildBlockResult {
    const {
        flowStore: { flows },
        builderUiStore: {
            setShowInvoiceOverlayForId,
            selectedFlow: { isLeadForm }
        }
    } = rootStore;

    let recurringScheduleBlock;
    let recurringPaymentBlock;
    let paymentScheduleBlock;
    let paymentBlock;
    let secondPaymentBlock;

    // This doesn't use createChildBlock because the adding of it happens
    // as part of the create commands
    const invoiceBlock = createBlockInstance<InvoiceBlock>(
        createBlockAttributes(parent, type, initialAttributes),
        parent.flowRevision,
        rootStore
    );

    const invoiceHeaderBlock = createChildBlock<InvoiceHeaderBlock>({
        rootStore,
        parent: invoiceBlock,
        type: 'invoice_header_block',
        order: 1
    });

    const servicesBlock = createChildBlock<ServicesBlock>({
        rootStore,
        parent: invoiceBlock,
        type: 'services_block',
        order: 3
    });

    // Auto create single service item if flow has no chargeable service selection blocks
    let serviceBlock = null;
    if (
        !rootStore.builderUiStore.selectedFlow
            ?.hasChargeableServiceSelectionBlocks
    ) {
        serviceBlock = createChildBlock<InvoiceServiceItemBlock>({
            rootStore,
            parent: servicesBlock,
            type: 'invoice_service_item_block'
        });
    }

    const columnHeaderBlock = createChildBlock<InvoiceColumnHeaderBlock>({
        rootStore,
        parent: invoiceBlock,
        order: 2,
        type: 'invoice_column_header_block'
    });

    const invoiceSummaryBlock = createChildBlock<InvoiceSummaryBlock>({
        rootStore,
        parent: invoiceBlock,
        type: 'invoice_summary_block',
        order: 4
    });

    if (invoiceBlock.invoice_type === 'payment_schedule') {
        paymentScheduleBlock = createChildBlock<PaymentScheduleBlock>({
            rootStore,
            parent: invoiceBlock,
            type: 'payment_schedule_block',
            order: 5
        });

        const opts = isLeadForm
            ? {
                  payment_content: {
                      amount_calc_type: AmountCalcType.DivideEqually,
                      date_calc_type: DateCalcType.Today
                  }
              }
            : {
                  payment_content: {
                      amount_calc_type: AmountCalcType.DivideEqually,
                      date_calc_type: DateCalcType.DateSent
                  }
              };

        paymentBlock = createChildBlock<PaymentBlock>({
            rootStore,
            parent: paymentScheduleBlock,
            type: 'payment_block',
            opts
        });
        if (!isLeadForm) {
            const projectDate = flows[parent.flow_id]?.project?.event_date;
            const secondPaymentOpts = calcSecondPaymentOpts(projectDate);

            secondPaymentBlock = createChildBlock<PaymentBlock>({
                rootStore,
                parent: paymentScheduleBlock,
                type: 'payment_block',
                opts: secondPaymentOpts
            });
        }
    } else if (invoiceBlock.invoice_type === 'recurring') {
        recurringScheduleBlock = createChildBlock<RecurringScheduleBlock>({
            rootStore,
            parent: invoiceBlock,
            type: 'recurring_schedule_block',
            order: 6
        });
        recurringPaymentBlock = createChildBlock<PaymentBlock>({
            rootStore,
            parent: recurringScheduleBlock,
            type: 'payment_block',
            opts: {
                payment_content: {
                    amount_calc_type: AmountCalcType.DivideEqually,
                    date_calc_type: DateCalcType.DateSent
                }
            }
        });
    }

    setShowInvoiceOverlayForId(invoiceBlock.block_id);

    const tempReturnValue = {
        newBlocksMap: {
            [invoiceBlock.block_id]: invoiceBlock,
            [invoiceHeaderBlock.block_id]: invoiceHeaderBlock,
            [invoiceSummaryBlock.block_id]: invoiceSummaryBlock,
            [servicesBlock.block_id]: servicesBlock,
            ...(serviceBlock && { [serviceBlock.block_id]: serviceBlock }),
            [columnHeaderBlock.block_id]: columnHeaderBlock
        },
        newTargetBlock: invoiceBlock,
        newChildLayout: { block_id: invoiceBlock.block_id, size: 1, order }
    };

    if (invoiceBlock.invoice_type === 'recurring') {
        tempReturnValue.newBlocksMap[recurringScheduleBlock.block_id] =
            recurringScheduleBlock;
        tempReturnValue.newBlocksMap[recurringPaymentBlock.block_id] =
            recurringPaymentBlock;
    } else if (invoiceBlock.invoice_type === 'payment_schedule') {
        tempReturnValue.newBlocksMap[paymentScheduleBlock.block_id] =
            paymentScheduleBlock;
        tempReturnValue.newBlocksMap[paymentBlock.block_id] = paymentBlock;
        if (secondPaymentBlock) {
            tempReturnValue.newBlocksMap[secondPaymentBlock.block_id] =
                secondPaymentBlock;
        }
    }

    return tempReturnValue;
}

function createPaymentScheduleBlock(
    parent: ContainerBlock,
    type: 'payment_schedule_block',
    order: number,
    rootStore: RootStore
): ICreateChildBlockResult {
    const paymentScheduleBlock = createBlockInstance<PaymentScheduleBlock>(
        createBlockAttributes(parent, type),
        parent.flowRevision,
        rootStore
    );

    const paymentBlock = createChildBlock<PaymentBlock>({
        rootStore,
        parent: paymentScheduleBlock,
        type: 'payment_block',
        opts: {
            payment_content: {
                amount_calc_type: AmountCalcType.DivideEqually,
                date_calc_type: DateCalcType.DateSent
            }
        }
    });

    const projectDate =
        rootStore.flowStore.flows[parent.flow_id]?.project?.event_date;
    const secondPaymentOpts = calcSecondPaymentOpts(projectDate);

    const secondPaymentBlock = createChildBlock<PaymentBlock>({
        rootStore,
        parent: paymentScheduleBlock,
        type: 'payment_block',
        opts: secondPaymentOpts
    });

    return {
        newBlocksMap: {
            [paymentScheduleBlock.block_id]: paymentScheduleBlock,
            [paymentBlock.block_id]: paymentBlock,
            ...(secondPaymentBlock && {
                [secondPaymentBlock.block_id]: secondPaymentBlock
            })
        },
        newTargetBlock: paymentScheduleBlock,
        newChildLayout: {
            block_id: paymentScheduleBlock.block_id,
            size: 20,
            order
        }
    };
}

function createRecurringScheduleBlock(
    parent: ContainerBlock,
    type: 'recurring_schedule_block',
    order: number,
    rootStore: RootStore
): ICreateChildBlockResult {
    const recurringScheduleBlock = createBlockInstance<RecurringScheduleBlock>(
        createBlockAttributes(parent, type),
        parent.flowRevision,
        rootStore
    );

    const recurringPaymentBlock = createChildBlock<PaymentBlock>({
        rootStore,
        parent: recurringScheduleBlock,
        type: 'payment_block',
        opts: {
            payment_content: {
                amount_calc_type: AmountCalcType.DivideEqually,
                date_calc_type: DateCalcType.DateSent
            }
        }
    });

    return {
        newBlocksMap: {
            [recurringScheduleBlock.block_id]: recurringScheduleBlock,
            [recurringPaymentBlock.block_id]: recurringPaymentBlock
        },
        newTargetBlock: recurringScheduleBlock,
        newChildLayout: {
            block_id: recurringScheduleBlock.block_id,
            size: 1,
            order
        }
    };
}

function createChargeContainerWithPayBlock(
    parent: ContainerBlock,
    type: 'charge_container_block',
    order: number,
    rootStore: RootStore
): ICreateChildBlockResult {
    const chargeContainerBlock = createBlockInstance<ChargeContainerBlock>(
        createBlockAttributes(parent, type),
        parent.flowRevision,
        rootStore
    );

    const chargeBlock = createChildBlock<ChargeBlock>({
        rootStore,
        parent: chargeContainerBlock,
        type: 'charge_block',
        order: 1
    });

    return {
        newBlocksMap: {
            [chargeContainerBlock.block_id]: chargeContainerBlock,
            [chargeBlock.block_id]: chargeBlock
        },
        newTargetBlock: chargeContainerBlock,
        newChildLayout: {
            block_id: chargeContainerBlock.block_id,
            size: 1,
            order
        }
    };
}

function createScheduleContainerBlock(
    parent: ContainerBlock,
    type: 'schedule_container_block',
    order: number,
    rootStore: RootStore
): ICreateChildBlockResult {
    const {
        builderUiStore: { setShowScheduleOverlayForId }
    } = rootStore;

    const scheduleContainerBlock = createBlockInstance<ScheduleContainerBlock>(
        createBlockAttributes(parent, type),
        parent.flowRevision,
        rootStore
    );

    const scheduleSessionBlock = createChildBlock<ScheduleSessionBlock>({
        rootStore,
        parent: scheduleContainerBlock,
        type: 'schedule_session_block',
        order: 1
    });

    setShowScheduleOverlayForId(scheduleContainerBlock.block_id);

    return {
        newBlocksMap: {
            [scheduleContainerBlock.block_id]: scheduleContainerBlock,
            [scheduleSessionBlock.block_id]: scheduleSessionBlock
        },
        newTargetBlock: scheduleContainerBlock,
        newChildLayout: {
            block_id: scheduleContainerBlock.block_id,
            size: 1,
            order
        }
    };
}

function createServiceSelectionBlock(
    parent: ContainerBlock,
    type: 'service_selection_block',
    order: number,
    rootStore: RootStore,
    initialAttributes?: Partial<ServiceSelectionBlock>
): ICreateChildBlockResult {
    const {
        builderUiStore: { setShowServicesOverlayForId }
    } = rootStore;

    const serviceSelectionBlock = createBlockInstance<ServiceSelectionBlock>(
        createBlockAttributes(parent, type, initialAttributes),
        parent.flowRevision,
        rootStore
    );

    setShowServicesOverlayForId(serviceSelectionBlock.block_id);

    const serviceItemsContainerBlock =
        createChildBlock<ServiceItemsContainerBlock>({
            rootStore,
            parent: serviceSelectionBlock,
            type: 'service_items_container_block',
            order: 1
        });

    const firstServiceItemBlock = createChildBlock<ServiceSelectionItemBlock>({
        rootStore,
        parent: serviceItemsContainerBlock,
        type: 'service_selection_item_block',
        order: 1
    });

    return {
        newBlocksMap: {
            [serviceSelectionBlock.block_id]: serviceSelectionBlock,
            [serviceItemsContainerBlock.block_id]: serviceItemsContainerBlock,
            [firstServiceItemBlock.block_id]: firstServiceItemBlock
        },
        newTargetBlock: serviceSelectionBlock,
        newChildLayout: {
            block_id: serviceSelectionBlock.block_id,
            size: 1,
            order
        }
    };
}

function createRowOfServicesBlock(
    parent: ServiceSelectionBlock,
    type: 'service_items_container_block',
    order: number,
    rootStore: RootStore
): ICreateChildBlockResult {
    const serviceItemsContainerBlock =
        createBlockInstance<ServiceItemsContainerBlock>(
            createBlockAttributes(parent, type),
            parent.flowRevision,
            rootStore
        );

    const firstServiceItemBlock = createChildBlock<ServiceSelectionItemBlock>({
        rootStore,
        parent: serviceItemsContainerBlock,
        type: 'service_selection_item_block',
        order: 1
    });

    return {
        newBlocksMap: {
            [serviceItemsContainerBlock.block_id]: serviceItemsContainerBlock,
            [firstServiceItemBlock.block_id]: firstServiceItemBlock
        },
        newTargetBlock: firstServiceItemBlock,
        newChildLayout: {
            block_id: serviceItemsContainerBlock.block_id,
            size: 1,
            order
        }
    };
}

function _createLayout(
    parent: ContainerBlock,
    struct: ICompositionLayoutStruct,
    compositionType: CompositionType,
    rootStore: RootStore,
    blocksConfig: ICreateChildBlockResult,
    order: number,
    blockSize?: number,
    initialAttributes?: any
) {
    // This doesn't use createChildBlock because the adding of it happens
    // as part of the create commands
    const childBlock = createBlockInstance<ContainerBlock>(
        createBlockAttributes(
            parent,
            struct.type,
            getLayoutBlockOptions(struct, rootStore),
            compositionType
        ),
        parent.flowRevision,
        rootStore,
        initialAttributes
    );

    // If Current block is a container block create all its children on the childBlock
    if (struct.children) {
        struct.children.forEach(child => {
            // childBlock is the new parent
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            createChildLayout(
                child,
                childBlock,
                null,
                rootStore,
                blocksConfig,
                null,
                child.size,
                initialAttributes
            );
        });
    }

    // order is here for the first parent root container
    blocksConfig.newBlocksMap[childBlock.block_id] = childBlock;

    return { block_id: childBlock.block_id, order, size: blockSize };
}

function createChildLayout(
    struct: ICompositionLayoutStruct,
    parent: ContainerBlock,
    compositionType: CompositionType,
    rootStore: RootStore,
    blocksConfig: ICreateChildBlockResult,
    order?: number,
    size?: number,
    initialAttributes?: any
): IChildLayout {
    const childLayout = _createLayout(
        parent,
        struct,
        compositionType,
        rootStore,
        blocksConfig,
        order,
        size,
        initialAttributes
    );
    parent.addChild(
        blocksConfig.newBlocksMap[childLayout.block_id],
        childLayout.order,
        childLayout.size
    );

    return childLayout;
}

function createLayout(
    struct: ICompositionLayoutStruct,
    parent: ContainerBlock,
    compositionType: CompositionType,
    rootStore: RootStore,
    blocksConfig: ICreateChildBlockResult,
    order?: number,
    initialAttributes?: any,
    size?: number
): IChildLayout {
    return _createLayout(
        parent,
        struct,
        compositionType,
        rootStore,
        blocksConfig,
        order,
        size,
        initialAttributes
    );
}

export function createCompositionLayoutBlock(
    parent: ContainerBlock,
    compositionType: CompositionType,
    order: number,
    rootStore: RootStore,
    layout: ICompositionLayoutStruct,
    initialAttributes
): ICreateChildBlockResult {
    const blocksConfig: ICreateChildBlockResult = {
        newBlocksMap: {},
        newTargetBlock: null,
        newChildLayout: null
    };

    blocksConfig.newChildLayout = createLayout(
        layout,
        parent,
        compositionType,
        rootStore,
        blocksConfig,
        order,
        initialAttributes
    );

    const childBlock = Object.values(blocksConfig.newBlocksMap).find(
        ({ block_id }) => block_id === blocksConfig.newChildLayout.block_id
    );
    blocksConfig.newTargetBlock =
        blocksConfig.newBlocksMap[childBlock.block_id];

    return blocksConfig;
}

function createWrappedChild(
    parent: ContainerBlock,
    type: TBlockType,
    order: number,
    rootStore: RootStore,
    initialAttributes: any = {}
): ICreateChildBlockResult {
    // If parent is a root block, this means we need to create row and column containers

    // This doesn't use createChildBlock because the adding of it happens
    // as part of the create commands
    const rootChildBlock = createBlockInstance<ContainerBlock>(
        createBlockAttributes(parent, 'container_block', {
            layout_type: 'horizontal'
        }),
        parent.flowRevision,
        rootStore
    );

    const columnBlock = createChildBlock<ContainerBlock>({
        rootStore,
        parent: rootChildBlock,
        type: 'container_block',
        opts: {
            layout_type: 'vertical'
        }
    });

    const newBlock = createChildBlock<ContainerBlock>({
        rootStore,
        parent: columnBlock,
        type,
        opts: initialAttributes
    });

    return {
        newBlocksMap: {
            [newBlock.block_id]: newBlock,
            [columnBlock.block_id]: columnBlock,
            [rootChildBlock.block_id]: rootChildBlock
        },
        newTargetBlock: newBlock,
        newChildLayout: {
            block_id: rootChildBlock.block_id,
            order,
            size: 1
        }
    };
}

export function createBlock(
    parent: ContainerBlock,
    type: TBlockType,
    order: number,
    rootStore: RootStore,
    initialAttributes = {}
): ICreateChildBlockResult {
    let createChildBlockResult: ICreateChildBlockResult;
    if (type === 'invoice_container_block') {
        createChildBlockResult = createInvoiceBlock(
            parent,
            type,
            order,
            rootStore,
            initialAttributes
        );
    } else if (type === 'contract_block') {
        createChildBlockResult = createContractBlock(
            parent,
            type,
            order,
            rootStore
        );
    } else if (type === 'charge_container_block') {
        createChildBlockResult = createChargeContainerWithPayBlock(
            parent,
            type,
            order,
            rootStore
        );
    } else if (type === 'payment_schedule_block') {
        createChildBlockResult = createPaymentScheduleBlock(
            parent,
            type,
            order,
            rootStore
        );
    } else if (type === 'recurring_schedule_block') {
        createChildBlockResult = createRecurringScheduleBlock(
            parent,
            type,
            order,
            rootStore
        );
    } else if (type === 'service_selection_block') {
        createChildBlockResult = createServiceSelectionBlock(
            parent,
            type,
            order,
            rootStore,
            initialAttributes
        );
    } else if (type === 'service_items_container_block') {
        createChildBlockResult = createRowOfServicesBlock(
            parent as unknown as ServiceSelectionBlock,
            type,
            order,
            rootStore
        );
    } else if (type === 'schedule_container_block') {
        createChildBlockResult = createScheduleContainerBlock(
            parent,
            type,
            order,
            rootStore
        );
    } else if (parent.isRootBlock) {
        createChildBlockResult = createWrappedChild(
            parent,
            type,
            order,
            rootStore,
            initialAttributes
        );
    } else {
        // This doesn't use createChildBlock because the adding of it happens
        // as part of the create commands
        const newBlock = createBlockInstance<TBlock>(
            createBlockAttributes(parent, type, initialAttributes),
            parent.flowRevision,
            rootStore,
            initialAttributes
        );

        createChildBlockResult = {
            newBlocksMap: {
                [newBlock.block_id]: newBlock
            },
            newTargetBlock: newBlock,
            // If parent is not the root
            newChildLayout: {
                block_id: newBlock.block_id,
                order,
                size: 1
            }
        };
    }

    return createChildBlockResult;
}

// The create method receives the block that the plus was clicked on (may be a bottom level block IE - ImageBlock, TextBlock)
// But the actual created block is created under the parent of the block that's plus was clicked
// This has two exceptions:
// 1. Root block - When creating the first child of the root block, the root block is both the
//    block being clicked on and the parent block to create under. This is handled by the caled where block.parent = false
// 2. A vertical container with no children - in this case the plus is associated to the vertical container
//    So it is both the block being clicked on and the block to create under.
// All other cases, the block to create user will be the parent on the block who's plus button was clicked, IE - block.parent
export function getParentBlockForCreate(block: TBlock, isParent = false) {
    if (
        (block.type === 'container_block' &&
            (block as ContainerBlock).layout_type === 'vertical') ||
        isParent
    ) {
        return block;
    }

    return block.parent ? block.parent : block;
}

export function getColumnsToDelete(columns: TBlock[], desiredColumns: number) {
    return columns.slice(desiredColumns);
}

function createAddColumnsActions(
    amountToAdd: number,
    block: ContainerBlock,
    rootStore: RootStore
): UpdateBlockColumnNumberValue {
    const commands: TCommand[] = [];
    const revertCommands: TCommand[] = [];
    const columnsToCreate = [];

    for (let index = 0; index < amountToAdd; index++) {
        columnsToCreate.push(
            createBlockInstance(
                createBlockAttributes(block, 'container_block', {
                    layout_type: 'vertical'
                }),
                block.flowRevision,
                rootStore
            )
        );
    }

    columnsToCreate.forEach(newBlock => {
        commands.push(createCreateBlockCommand(newBlock));

        revertCommands.push(createDeleteBlockCommand(newBlock.block_id));
    });

    const newChildrenSize = 100 / (block.children.length + amountToAdd);
    block.children.forEach(child => {
        child.size = newChildrenSize;
    });
    commands.push(
        createUpdateBlockCommand(block.block_id, {
            children: [
                ...block.children,
                ...columnsToCreate.map((newBlock, i) => ({
                    block_id: newBlock.block_id,
                    size: newChildrenSize,
                    order: block.maxBlockOrder + i + 1
                }))
            ]
        })
    );

    revertCommands.push(
        createUpdateBlockCommand(block.block_id, {
            children: [...block.children]
        })
    );

    return {
        commands,
        revertCommands,
        createdVerticalContainers: columnsToCreate
    };
}

function removeNonDuplicatableAttributes(
    attrs: TBlockDataForServer,
    completeCopy: boolean
): TBlockDataForServer {
    if (completeCopy) {
        return attrs;
    }

    switch (attrs.type) {
        case 'question_block':
            return {
                ...attrs,
                question: {
                    ...(attrs as QuestionBlockDataForServer).question,
                    connected_property: ''
                }
            };
        default:
            return attrs;
    }
}

export interface _DuplicateBlockOptions {
    completeCopy?: boolean;
    createdBlocks?: Array<any>;
    newRootBlockData?: any;
    fetchParent?: boolean;
    duplicateChildren?: boolean;
    withAttributions?: boolean;
}

const default_DuplicateBlockOptions: _DuplicateBlockOptions = {
    completeCopy: false,
    createdBlocks: [],
    newRootBlockData: null,
    duplicateChildren: true
};

interface IDuplicateBlockHelperValue {
    newBlock: TBlock;
    createdBlocks: Array<TBlock>;
}

function getActualParent(
    parent: ContainerableBlock,
    blockAttrs: TBlockDataForServer,
    newRootBlockData
) {
    if (parent || !newRootBlockData) {
        return parent;
    }

    return blockAttrs.block_id !== newRootBlockData.block_id
        ? newRootBlockData
        : undefined;
}

const isAttributable = (
    blockToAttribute: BaseBlock,
    parentBlock?: BaseBlock
) => {
    return (
        blockToAttribute.attributable ||
        !parentBlock ||
        (parentBlock.page && parentBlock.isRootBlock)
    );
};

// Get a block and recursively duplicate it and it's children
export function _duplicateBlock(
    rootStore: RootStore,
    block: TBlock,
    blockAttributes: any,
    parent: ContainerableBlock,
    {
        completeCopy,
        createdBlocks = [],
        newRootBlockData = null,
        duplicateChildren = true,
        withAttributions = false
    }: _DuplicateBlockOptions = default_DuplicateBlockOptions,
    allRelevantBlocks?: TBlock[]
): IDuplicateBlockHelperValue {
    let duplicatedChildren = [];
    let tmpCreatedBlocks = [...createdBlocks];
    // remove block_id from blockAttrs passed to createBlockAttributes
    // because that function already generates a new block_id and this one overwrites it
    // also remove page_id because we need to take it from the parent
    const blockAttrs = removeNonDuplicatableAttributes(
        block.getDataForServer(),
        completeCopy
    );

    if (!completeCopy) {
        delete blockAttrs.block_id;
        delete blockAttrs.page_id;
        if (blockAttributes?.flowId) {
            delete blockAttrs.flow_id;
        }
    }

    const actualParent = getActualParent(parent, blockAttrs, newRootBlockData);
    const newBlock = createBlockInstance(
        {
            ...createBlockAttributes(actualParent, block.type, {
                ...blockAttrs,
                ...(withAttributions &&
                    isAttributable(block, parent) && {
                        source_block_id: block.block_id,
                        source_flow_id: block.flow_id
                    })
            })
        },
        block.flowRevision,
        rootStore
    );

    if (newBlock instanceof ContainerableBlock && duplicateChildren) {
        let { childBlocks } = newBlock;
        // allows you to send all the blocks that should be duplicated and not rely on flowRevision to get full block details
        // childBlocks is @calculated to use flowRevision.blocks[child.block_id]
        // so you have a strond dependency on revision - sending allRelevantBlocks gives a different source of block data to the function
        if (allRelevantBlocks) {
            const blockIdToValueMap = allRelevantBlocks.reduce(
                (map, current) => ({ ...map, [current.block_id]: current }),
                {}
            );

            childBlocks = newBlock.sortedChildLayouts.map(
                child => blockIdToValueMap[child.block_id]
            );
        }

        // recursively duplicate children
        duplicatedChildren = childBlocks.map(child => {
            const { newBlock: _newBlock, createdBlocks: _createdBlocks } =
                _duplicateBlock(
                    rootStore,
                    child,
                    blockAttributes,
                    <ContainerableBlock>newBlock,
                    {
                        completeCopy,
                        createdBlocks: tmpCreatedBlocks,
                        newRootBlockData,
                        // attribute only root and its direct children
                        withAttributions:
                            withAttributions && isAttributable(child, block)
                    },
                    allRelevantBlocks
                );

            if (_newBlock instanceof ContractHTMLBlock && !completeCopy) {
                const newContract = GenerateSmartFieldsID(_newBlock);

                _newBlock.contract = newContract;
            }

            tmpCreatedBlocks = _createdBlocks;
            return _newBlock;
        });

        // update existing children pointers with the block_ids of the
        // newly duplicated child blocks
        newBlock.children = newBlock.sortedChildLayouts.map((child, i) => ({
            ...child,
            block_id: duplicatedChildren[i].block_id
        }));
    }

    if (blockAttributes?.flow_id) {
        newBlock.setFlowId(blockAttributes.flow_id);
    }

    if (blockAttributes?.flowRevision) {
        newBlock.setFlowRevision(blockAttributes.flowRevision);
    }

    return {
        newBlock,
        createdBlocks: [...tmpCreatedBlocks, newBlock]
    };
}

interface DuplicateBlockOptions extends _DuplicateBlockOptions {
    duplicateChildren?: boolean;
    isImporting?: boolean;
    ctx?: IDuplicateBlockContext;
}

const defaultDuplicateBlockOptions: DuplicateBlockOptions = {
    completeCopy: false,
    duplicateChildren: true,
    isImporting: false
};

export function duplicateBlock(
    block: TBlock,
    rootStore: RootStore,
    order: number,
    options: DuplicateBlockOptions = defaultDuplicateBlockOptions
): TCommand[] {
    const { createdBlocks, newBlock: duplicatedBlock } = _duplicateBlock(
        rootStore,
        block,
        {},
        block.parent,
        options
    );

    if (options.ctx) {
        options.ctx.duplicate_block = duplicatedBlock;
    }

    const createdBlocksCmd: TCommand[] = createdBlocks.map(blk =>
        createCreateBlockCommand(blk)
    );

    const parentChildren = block.parent ? block.parent.children : [];
    const newChildren = options.completeCopy
        ? [...block.parent.children]
        : [
              ...parentChildren,
              { block_id: duplicatedBlock.block_id, size: 1, order }
          ];

    const updatedBlocks: TCommand[] = block.parent
        ? [
              createUpdateBlockCommand(block.parent.block_id, {
                  children: newChildren
              })
          ]
        : [];

    return [...createdBlocksCmd, ...updatedBlocks];
}

export function createRevertDuplicateBlock(
    blockToDuplicate: TBlock,
    commands: TCommand[]
) {
    const revertCommands: TCommand[] = commands
        .filter(x => x.type === CommandType.Create)
        .map(({ data }) => createDeleteBlockCommand((data as TBlock).block_id));

    const parentBlock = blockToDuplicate.parent;
    if (parentBlock) {
        revertCommands.push(
            createUpdateBlockCommand(parentBlock.block_id, {
                children: parentBlock.children
            })
        );
    }

    return revertCommands;
}

export function translateHTMLEntitiesForComparison(htmlString: string) {
    const HtmlEntitiesRegex = /(&nbsp;|&amp;|&quot;|&lt;|&gt;|\n|')/g;
    const htmlEntityTranslation = {
        '&nbsp;': ' ',
        '&amp;': '&',
        '&quot;': '"',
        '&lt;': '<',
        '&gt;': '>',
        '\n': '',
        "'": '"'
    };
    function translator(match) {
        return htmlEntityTranslation[match];
    }
    return htmlString.replace(HtmlEntitiesRegex, translator);
}

export function PasteBlockFromClipboard({
    allRelevantBlocks,
    block,
    options = defaultDuplicateBlockOptions,
    order,
    originalBlockToReplace,
    parent,
    rootStore
}: {
    allRelevantBlocks: TBlock[];
    block: TBlock;
    options: DuplicateBlockOptions;
    order: number;
    originalBlockToReplace: TBlock;
    parent: ContainerableBlock;
    rootStore: RootStore;
}): { commands: TCommand[]; revertCommands: TCommand[] } {
    const { createdBlocks, newBlock: duplicatedBlock } = _duplicateBlock(
        rootStore,
        block,
        {
            flow_id: parent.flow_id,
            flowRevision: parent.flowRevision
        },
        parent,
        options,
        allRelevantBlocks
    );

    if (options.ctx) {
        options.ctx.duplicate_block = duplicatedBlock;
    }

    const createdBlocksCmd = createdBlocks.map(blk =>
        createCreateBlockCommand(blk)
    );

    const newChildren = [
        ...duplicatedBlock.parent.children,
        { block_id: duplicatedBlock.block_id, size: 1, order }
    ];

    const updatedBlock = createUpdateBlockCommand(
        duplicatedBlock.parent.block_id,
        {
            children: newChildren
        }
    );

    const deleteBlocksCmd = createdBlocks.map(({ block_id }) =>
        createDeleteBlockCommand(block_id)
    );
    deleteBlocksCmd.push(
        createDeleteBlockCommand(originalBlockToReplace.block_id)
    );

    return {
        commands: [...createdBlocksCmd, updatedBlock],
        revertCommands: deleteBlocksCmd
    };
}

export function UpdateBlockToTemplate(
    block: TBlock,
    parent: ContainerableBlock,
    blockType: TBlockType,
    order: number,
    templateId: string,
    rootStore: RootStore,
    options: DuplicateBlockOptions = defaultDuplicateBlockOptions
): { commands: TCommand[]; revertCommands: TCommand[] } {
    const { createdBlocks, newBlock: duplicatedBlock } = _duplicateBlock(
        rootStore,
        block,
        {
            flow_id: parent.flow_id,
            flowRevision: parent.flowRevision
        },
        parent,
        { ...options, fetchParent: true, withAttributions: true }
    );

    if (templateId) {
        (<ContainerableBlock>duplicatedBlock).source_flow_id = templateId;
    }

    if (options.ctx) {
        options.ctx.duplicate_block = duplicatedBlock;
    }

    const createdBlocksCmd = createdBlocks.map(blk =>
        createCreateBlockCommand(blk)
    );

    const newChildren = [
        ...duplicatedBlock.parent.children,
        { block_id: duplicatedBlock.block_id, size: 1, order }
    ];

    const revertUpdatedBlock = createUpdateBlockCommand(
        duplicatedBlock.parent.block_id,
        {
            children: duplicatedBlock.parent.children
        }
    );

    const updatedBlock = createUpdateBlockCommand(
        duplicatedBlock.parent.block_id,
        {
            children: newChildren
        }
    );

    const deleteBlocksCmd = createdBlocks.map(({ block_id }) =>
        createDeleteBlockCommand(block_id)
    );

    return {
        commands: [...createdBlocksCmd, updatedBlock],
        revertCommands: [...deleteBlocksCmd, revertUpdatedBlock]
    };
}

function createRemoveColumnsActions(
    block: ContainerBlock,
    nColumns: number,
    currColumns,
    rootStore: RootStore
): UpdateBlockColumnNumberValue {
    const commands: TCommand[] = [];
    let revertCommands: TCommand[] = [];
    const columnsToDelete = (block.childBlocks as ContainerBlock[]).slice(
        nColumns,
        currColumns
    );

    columnsToDelete.forEach(blockToDelete => {
        commands.push(createDeleteBlockCommand(blockToDelete.block_id));

        revertCommands = revertCommands.concat(
            duplicateBlock(blockToDelete, rootStore, blockToDelete.order, {
                completeCopy: true,
                duplicateChildren: false
            })
        );
    });

    const columnsToDeleteIds = columnsToDelete.map(column => column.block_id);

    commands.push(
        createUpdateBlockCommand(block.block_id, {
            children: block.children.filter(
                child => !columnsToDeleteIds.includes(child.block_id)
            )
        })
    );

    revertCommands.push(
        createUpdateBlockCommand(block.block_id, {
            children: [...block.children]
        })
    );

    // TODO: find correct lib field values in tsconfig.json
    // @ts-ignore
    const childrenToMove = columnsToDelete.flatMap(blk => blk.childBlocks);
    const columnToMoveTo = <ContainerBlock>block.childBlocks[nColumns - 1];

    const newChildrenSize =
        100 / (block.children.length - columnsToDelete.length);
    block.children.forEach(child => {
        child.size = newChildrenSize;
    });
    if (childrenToMove.length) {
        commands.push(
            createUpdateBlockCommand(columnToMoveTo.block_id, {
                children: [
                    ...columnToMoveTo.children,
                    ...childrenToMove.map(blockToUpdate => ({
                        block_id: blockToUpdate.block_id,
                        order: 1,
                        size: newChildrenSize
                    }))
                ]
            })
        );

        revertCommands.push(
            createUpdateBlockCommand(columnToMoveTo.block_id, {
                children: [...columnToMoveTo.children]
            })
        );
    }

    // Update parent_id for all moving blocks
    const movedChildrenNewParents = {};
    const movedChildrenOldParents = {};
    childrenToMove.forEach(({ block_id }) => {
        movedChildrenNewParents[block_id] = columnToMoveTo.block_id;
    });
    childrenToMove.forEach(({ block_id, parent_id }) => {
        movedChildrenOldParents[block_id] = parent_id;
    });

    return {
        commands,
        revertCommands,
        movedChildrenOldParents,
        movedChildrenNewParents
    };
}

interface UpdateBlockColumnNumberValue extends IBasicCommandsActionValue {
    movedChildrenNewParents?: Record<string, string>;
    movedChildrenOldParents?: Record<string, string>;
    createdVerticalContainers?: Array<ContainerBlock>;
}

export function bulkBlocksUpdate(
    oldData: Array<Partial<TBlock>>,
    data: Array<Partial<TBlock>>
): IBasicCommandsActionValue {
    const commands: TCommand[] = [];
    const revertCommands: TCommand[] = [];
    data.forEach(newBlockData => {
        commands.push(
            createUpdateBlockCommand(newBlockData.block_id, {
                ...newBlockData
            })
        );
    });

    oldData.forEach(oldBlockData => {
        revertCommands.push(
            createUpdateBlockCommand(oldBlockData.block_id, {
                ...oldBlockData
            })
        );
    });

    return { commands, revertCommands };
}

export function updateBlockColumnNumber(
    block: ContainerBlock,
    nColumns: number,
    rootStore: RootStore
): UpdateBlockColumnNumberValue {
    const currColumns = block.children.length;

    // Creating new columns
    if (currColumns < nColumns) {
        return createAddColumnsActions(
            nColumns - currColumns,
            block,
            rootStore
        );
    }

    // Removing existing columns
    return createRemoveColumnsActions(block, nColumns, currColumns, rootStore);
}

interface IServiceSelectionLayoutGenerateData {
    chunksByLayout: Array<Array<Array<TBlock>>>;
    oldContainers: Array<ServiceItemsContainerBlock>;
}

function createServiceSelectionRowsChunks(
    serviceSelectionBlock: ServiceSelectionBlock,
    newLayout: ServicesItemsLayoutType
): IServiceSelectionLayoutGenerateData {
    const newColumnsCount = itemsPerContainer(newLayout);
    const chunksByBlockType: Array<Array<TBlock>> = [[]];
    const oldContainers: Array<ServiceItemsContainerBlock> = [];

    serviceSelectionBlock.childBlocks.forEach(childBlock => {
        if (childBlock instanceof ServiceItemsContainerBlock) {
            const serviceItemsChunk = last(chunksByBlockType);
            (
                childBlock as ServiceItemsContainerBlock
            ).serviceItemBlocks.forEach(x => serviceItemsChunk.push(x));

            oldContainers.push(childBlock);

            return;
        }

        // Each not service item block has it own row so we want to give him his own chunk and create a new chunk for the next items
        chunksByBlockType.push([childBlock]);
        chunksByBlockType.push([]);
    });

    // Remove all the empty chunks and split each chunk by the size of the desired columns count
    return {
        chunksByLayout: chunksByBlockType
            .filter(x => x.length)
            .map(itemsChunk => chunk(itemsChunk, newColumnsCount)),
        oldContainers
    };
}

function getServiceSelectionContainerForRow(
    oldContainers: Array<ServiceItemsContainerBlock>,
    serviceSelectionBlock: ServiceSelectionBlock,
    actionData: UpdateBlockColumnNumberValue,
    rootStore: RootStore
) {
    const { commands, revertCommands } = actionData;

    if (oldContainers.length) {
        const containerBlock = oldContainers.shift();
        revertCommands.push(
            createUpdateBlockCommand(containerBlock.block_id, {
                children: [...containerBlock.children]
            })
        );

        return containerBlock;
    }

    const newContainerBlock = createBlockInstance(
        createBlockAttributes(
            serviceSelectionBlock,
            'service_items_container_block',
            {
                layout_type: 'vertical'
            }
        ),
        serviceSelectionBlock.flowRevision,
        rootStore
    );

    commands.push(createCreateBlockCommand(newContainerBlock));
    revertCommands.push(createDeleteBlockCommand(newContainerBlock.block_id));

    return newContainerBlock;
}

function convertRowToActionData(
    blocksRow: Array<TBlock>,
    oldContainers: Array<ServiceItemsContainerBlock>,
    newRootChildren: any[],
    serviceSelectionBlock: ServiceSelectionBlock,
    actionData: UpdateBlockColumnNumberValue,
    rootStore: RootStore
) {
    const { commands, movedChildrenNewParents, movedChildrenOldParents } =
        actionData;

    const firstBlockInRow = blocksRow[0];

    // Handle not service item row
    if (!(firstBlockInRow instanceof ServiceSelectionItemBlock)) {
        // In a row of not a service item block there is only one item.
        newRootChildren.push(firstBlockInRow);

        movedChildrenNewParents[firstBlockInRow.block_id] =
            serviceSelectionBlock.block_id;
        movedChildrenOldParents[firstBlockInRow.block_id] =
            firstBlockInRow.parent_id;

        return;
    }

    const containerBlock = getServiceSelectionContainerForRow(
        oldContainers,
        serviceSelectionBlock,
        actionData,
        rootStore
    );
    newRootChildren.push(containerBlock);

    const children = blocksRow.map((block, index) => {
        movedChildrenNewParents[block.block_id] = containerBlock.block_id;
        movedChildrenOldParents[block.block_id] = block.parent_id;

        return {
            block_id: block.block_id,
            order: index + 1,
            size: 1
        };
    });

    commands.push(
        createUpdateBlockCommand(containerBlock.block_id, {
            children
        })
    );
}

function updateRootServiceSelectionBlockChildren(
    serviceSelectionBlock: ServiceSelectionBlock,
    finalContainers: any[],
    newLayout: ServicesItemsLayoutType,
    actionData: UpdateBlockColumnNumberValue
) {
    const { commands, revertCommands } = actionData;

    const rootChildren = finalContainers.map((block, index) => ({
        block_id: block.block_id,
        order: index + 1,
        size: 1
    }));

    commands.push(
        createUpdateBlockCommand(serviceSelectionBlock.block_id, {
            children: rootChildren,
            services_layout: newLayout
        })
    );

    revertCommands.push(
        createUpdateBlockCommand(serviceSelectionBlock.block_id, {
            children: [...serviceSelectionBlock.children],
            services_layout: serviceSelectionBlock.services_layout
        })
    );
}

function deleteUnusedContainers(
    oldContainers: Array<ServiceItemsContainerBlock>,
    actionData: UpdateBlockColumnNumberValue
) {
    const { commands, revertCommands } = actionData;

    oldContainers.forEach(oldContainer => {
        commands.push(createDeleteBlockCommand(oldContainer.block_id));
        revertCommands.push(createCreateBlockCommand(oldContainer));
    });
}

function convertLayoutToServiceSelectionLayoutUpdate(
    chunksByLayout: Array<Array<Array<TBlock>>>,
    oldContainers: Array<ServiceItemsContainerBlock>,
    serviceSelectionBlock: ServiceSelectionBlock,
    newLayout: ServicesItemsLayoutType,
    rootStore: RootStore
) {
    const actionData: UpdateBlockColumnNumberValue = {
        commands: [],
        revertCommands: [],
        // Update parent_id for all moving blocks
        movedChildrenNewParents: {},
        movedChildrenOldParents: {}
    };
    const newRootChildren = [];

    chunksByLayout.forEach(chunkByLayout => {
        chunkByLayout.forEach(blocksRow => {
            convertRowToActionData(
                blocksRow,
                oldContainers,
                newRootChildren,
                serviceSelectionBlock,
                actionData,
                rootStore
            );
        });
    });

    updateRootServiceSelectionBlockChildren(
        serviceSelectionBlock,
        newRootChildren,
        newLayout,
        actionData
    );

    deleteUnusedContainers(oldContainers, actionData);

    return actionData;
}

export function updateServiceSelectionLayout(
    serviceSelectionBlock: ServiceSelectionBlock,
    newLayout: ServicesItemsLayoutType,
    rootStore: RootStore
) {
    const { chunksByLayout, oldContainers } = createServiceSelectionRowsChunks(
        serviceSelectionBlock,
        newLayout
    );

    // The generated chunks structure is a mirror using arrays of the new layout structure of the service selection items.
    // Now we just need to take the structure and convert it to actual blocks

    return convertLayoutToServiceSelectionLayoutUpdate(
        chunksByLayout,
        oldContainers,
        serviceSelectionBlock,
        newLayout,
        rootStore
    );
}

// TODO: keeing this for possible future usage
// it's a simpler version of our current cloneBlock functionality
// function cloneBlock<NewBlockType extends TBlock>({
//     block,
//     newType,
//     newParent,
//     rootStore,
//     keysToExclude,
//     opts
// }: {
//     block: TBlock;
//     newType: TBlockType;
//     newParent: ContainerableBlock;
//     rootStore: RootStore;
//     keysToExclude: string[];
//     opts: any;
// }) {
//     const commands: TCommand[] = [];
//     const revertCommands: TCommand[] = [];

//     const parent = newParent || block.parent;
//     const type = newType || block.type;

//     const newBlockAttributes = removeKeysFromObject(
//         block.getDataForServer(),
//         keysToExclude
//     );
//     const newBlock = createBlockInstance<NewBlockType>(
//         createBlockAttributes(parent, type, {
//             ...newBlockAttributes,
//             ...opts
//         }),
//         block.flowRevision,
//         rootStore
//     );

//     // Create commands
//     commands.push(createCreateBlockCommand(newBlock));
//     revertCommands.push(createDeleteBlockCommand(newBlock.block_id));

//     // Update commands
//     commands.push(
//         createUpdateBlockCommand(parent.block_id, {
//             children: [
//                 ...parent.children,
//                 {
//                     block_id: newBlock.block_id,
//                     size: 1,
//                     order: parent.maxBlockOrder + 1
//                 }
//             ]
//         })
//     );

//     revertCommands.push(
//         createUpdateBlockCommand(parent.block_id, {
//             children: [...parent.children]
//         })
//     );

//     return { commands, revertCommands, newBlock };
// }

export interface IValidationBlockErrors {
    blockId: string;
    blockErrors: string[];
}

export interface IDeleteBlockOptions {
    shouldCreateRevertCommands?: boolean;
    calledFromReplaceTemplate?: boolean;
    applyResolvedCallback?: () => void;
}

export const DefaultDeleteBlockOptions: IDeleteBlockOptions = {
    shouldCreateRevertCommands: true,
    calledFromReplaceTemplate: false,
    applyResolvedCallback: () => {}
};

export const BlockTemplateDeleteBlockOptions: IDeleteBlockOptions = {
    shouldCreateRevertCommands: true,
    calledFromReplaceTemplate: true
};

export function deleteBlock(
    block: TBlock,
    rootStore: RootStore,
    {
        shouldCreateRevertCommands = true
    }: IDeleteBlockOptions = DefaultDeleteBlockOptions
): IBasicCommandsActionValue {
    let commands: TCommand[] = [];
    // remove block from parent

    const revertCommands = shouldCreateRevertCommands
        ? duplicateBlock(block, rootStore, block.order, {
              completeCopy: true
          })
        : [];

    if (block.parent) {
        const newChildrenOfParent = block.parent.children.filter(child => {
            return child.block_id !== block.block_id;
        });

        // Updates
        commands.push(
            createUpdateBlockCommand(block.parent_id, {
                children: newChildrenOfParent
            })
        );
    }

    // Delete block itself
    commands.push(createDeleteBlockCommand(block.block_id));

    if (block instanceof ContainerableBlock) {
        const allDescendants = (block as ContainerBlock).allDescendants();

        commands = commands.concat(
            allDescendants.map(_block =>
                createDeleteBlockCommand(_block.block_id)
            )
        );
    }

    return { commands, revertCommands };
}

export function moveBlock(
    block: TBlock,
    parent: ContainerBlock,
    order: number
) {
    const actions: TCommand[] = [];

    const childrenWithoutMovedBlock = block.parent.children.filter(
        child => child.block_id !== block.block_id
    );

    // If parent is the same parent (IE - this is a reorder and not a move between two block)
    // we need to use the filtered children so that the moved child doesn't get added twice
    const childrenOfNewParent =
        block.parent.block_id === parent.block_id
            ? childrenWithoutMovedBlock
            : parent.children;

    // Remove block from it's current parent
    actions.push(
        createUpdateBlockCommand(block.parent.block_id, {
            children: childrenWithoutMovedBlock
        })
    );

    // Update new parent to contain block as child
    actions.push(
        createUpdateBlockCommand(parent.block_id, {
            children: [
                ...childrenOfNewParent,
                {
                    block_id: block.block_id,
                    size: 1,
                    order
                }
            ]
        })
    );

    // If block was moved to another page, we need to update the page id
    if (block.page_id !== parent.page_id) {
        actions.push(
            createUpdateBlockCommand(block.block_id, {
                page_id: parent.page_id
            })
        );

        if (block instanceof ContainerableBlock) {
            const blocksToUpdate = (
                block as ContainerableBlock
            ).allDescendants();
            blocksToUpdate.forEach(childBlock => {
                actions.push(
                    createUpdateBlockCommand(childBlock.block_id, {
                        page_id: parent.page_id
                    })
                );
            });
        }
    }

    return actions;
}

export function duplicateRootBlock(
    rootBlock: ContainerBlock,
    rootStore: RootStore,
    newRootBlockData,
    {
        completeCopy = defaultDuplicateBlockOptions.completeCopy,
        isImporting,
        withAttributions
    }: DuplicateBlockOptions = defaultDuplicateBlockOptions
) {
    const { createdBlocks, newBlock: duplicatedBlock } = _duplicateBlock(
        rootStore,
        rootBlock,
        isImporting
            ? {
                  flowId: newRootBlockData.flow_id,
                  flowRevision:
                      rootStore.builderUiStore.selectedFlow.flowRevision
              }
            : {},
        null,
        {
            completeCopy,
            createdBlocks: [],
            newRootBlockData,
            withAttributions
        }
    );

    const duplicatedRootBlock = duplicatedBlock as ContainerBlock;
    duplicatedRootBlock.background_image = rootBlock.background_image;

    const createdBlocksCmd: TCommand[] = createdBlocks.map(blk =>
        createCreateBlockCommand(blk)
    );

    return {
        duplicatedBlock,
        createdBlocks,
        rootBlockCommands: [...createdBlocksCmd]
    };
}

export function getIsBlockDimmed(
    isThereASelectedBlock: boolean,
    selected: boolean,
    isNavigationBlockSelected: boolean,
    iAmTheParentOfTheSelectedBlock: boolean
) {
    const someoneIsSelectedButMe = isThereASelectedBlock && !selected;
    return (
        isNavigationBlockSelected ||
        (someoneIsSelectedButMe && !iAmTheParentOfTheSelectedBlock)
    );
}

export function getCreateState(
    blockId: string,
    shownPlusId: string,
    isInCreateMode: boolean
) {
    return {
        isTopPanelExpanded: shownPlusId === `${blockId}top` && isInCreateMode,
        isBottomPanelExpanded:
            shownPlusId === `${blockId}bottom` && isInCreateMode
    };
}

export function getHitZoneDiameter(
    isRootLevel: boolean,
    elementHeight: number
) {
    const defaultDiameter = isRootLevel ? 80 : 50;
    return elementHeight && elementHeight < defaultDiameter * 2
        ? elementHeight / 2
        : defaultDiameter;
}

export function createMoveBlockCommands(
    flow: Flow,
    blockId: string,
    oldParentId: string,
    action: Action<IMoveBlockContext>,
    order: number
): TCommand[] {
    const block = flow.draftRevision.getBlock(blockId);
    const parent = flow.draftRevision.getBlock(oldParentId);

    return moveBlock(block, parent as ContainerBlock, order);
}

export function createRevertMoveBlockCommands(
    flow: Flow,
    blockId: string,
    newParentId: string,
    oldParentId: string
): TCommand[] {
    const newParent = <ContainerableBlock>(
        flow.draftRevision.getBlock(newParentId)
    );
    const oldParent = <ContainerableBlock>(
        flow.draftRevision.getBlock(oldParentId)
    );

    const block = flow.draftRevision.getBlock(blockId);

    const revertCommands = [
        createUpdateBlockCommand(newParent.block_id, {
            children: [...newParent.children]
        }),
        createUpdateBlockCommand(oldParent.block_id, {
            children: [...oldParent.children]
        })
    ];

    // if reverting move to a different page, revert to the old page id
    if (oldParent.page_id !== newParent.page_id) {
        revertCommands.push(
            createUpdateBlockCommand(block.block_id, {
                page_id: oldParent.page_id
            })
        );

        if (block instanceof ContainerableBlock) {
            const blocksToUpdate = (
                block as ContainerableBlock
            ).allDescendants();
            blocksToUpdate.forEach(childBlock => {
                revertCommands.push(
                    createUpdateBlockCommand(childBlock.block_id, {
                        page_id: oldParent.page_id
                    })
                );
            });
        }
    }

    return revertCommands;
}

export function getExtraPaddingSize(block) {
    if (!block.shouldExpandOnSelect) {
        return 0;
    }

    const paddingY = block.styles?.padding_y;
    const customPaddingY = block.styles?.custom_padding_y;
    const padding =
        TOTAL_EXTRA_PADDING_SIZE - (paddingYMap[paddingY] ?? customPaddingY) ||
        0;
    return padding < 0 ? 0 : padding;
}

export function createCompanyInfoBlockOnPage(
    flow: Flow,
    blockId: string,
    rootBlockOfPage: TBlock,
    blockCtrl: BlockController,
    currCompany: Company
): TCommand[] {
    const companyCtx: ICreateBlockContext = {
        flow,
        compositionType: CompositionType.CompanyInfo,
        blockId,
        blockType: 'company_info_block',
        order: 0
    };

    const layout =
        getCompanyInfoLayouts(currCompany).layouts[
            CompanyInfoLayoutType.Centered
        ].layoutStruct;

    const { commands: companyCommands } =
        blockCtrl.createApplyCreateBlocksCommands(
            rootBlockOfPage,
            'company_info_block',
            companyCtx,
            layout,
            null
        );

    return companyCommands;
}

export function calculateSmartFieldOptionName(option: IContractDropdownField) {
    const parts = [
        option.data_source_property,
        option.data_source_type,
        option.category
    ];

    if (!isUndefined(option.data_source_sub_property)) {
        parts.unshift(option.data_source_sub_property);
    }

    return parts.join('');
}

export function getBaseCompositionTypeFromBlock(type: TBlockType) {
    return []
        .concat(BASE_BLOCK_TYPES, BASE_ACTION_BLOCK_TYPES)
        .find(block => block.type === type)?.compositionType as CompositionType;
}

const HB_PUBLISHER_MOCK_COMPANY_INFO: Partial<Company> = {
    company_name: 'HoneyBook',
    owner: {
        _id: '1234',
        email: 'hello@yourdomain.com',
        full_name: 'HoneyBook',
        initials: 'HB',
        system_user_type: 'vendor'
    },
    street_address: '123 Your Address',
    zip_code: 'Zip',
    city: 'City',
    state: 'State',
    phone_number: '(123) 456-7890',
    email: 'hello@yourdomain.com',
    icon_image: {
        url: 'https://res.cloudinary.com/honeybook/image/upload/v1722413314/company/64b9a0cec6193013790ac64a/icon/my_co_company_icon_p9thsh.png',
        cloudinary_public_id:
            'company/64b9a0cec6193013790ac64a/icon/my_co_company_icon_p9thsh.png',
        height: 100,
        width: 100,
        position_h: 100,
        position_w: 100,
        position_x: 0,
        position_y: 0
    }
};

const HONEYBOOK_CREATORS_ID: string[] = ['64b9a0cec6193013790ac64a'];

function checkIfHoneyBookPublisher(flow: Flow | CXFlow) {
    return HONEYBOOK_CREATORS_ID.includes(flow.company_id);
}

export function setCompanyInfoOnDescendants(
    block: CompanyInfoBlock | CXCompanyInfoBlock
) {
    if (
        !block.flow ||
        !block.children.length ||
        (!block.linked_to_company_settings &&
            (block.flow.is_template ||
                block.flow.isLeadForm ||
                !block.connected_variable_id))
    ) {
        return;
    }
    const [ConatinerableModel, ImageModel, TextModel] =
        block instanceof CompanyInfoBlock
            ? [ContainerableBlock, ImageBlock, TextBlock]
            : [CXContainerableBlock, CXImageBlock, CXTextBlock];

    const isHoneyBookPublisher = checkIfHoneyBookPublisher(block.flow);
    const shouldUseHbMockCompanyData =
        isHoneyBookPublisher && isPublicTemplateViewMode();
    const isUsingConnectedVariable =
        (!block.flow.is_template || isPublicTemplateViewMode()) &&
        block.connectedVariable;

    let companyInfo: Partial<Company>;
    if (shouldUseHbMockCompanyData) {
        companyInfo = HB_PUBLISHER_MOCK_COMPANY_INFO;
    } else if (isUsingConnectedVariable) {
        companyInfo = block.connectedVariable.value;
    } else {
        companyInfo = block.rootStore.companyStore.currCompany;
    }

    const { layoutStruct } =
        getCompanyInfoLayouts(companyInfo).layouts[
            block.company_info_layout_type || DEFAULT_COMPANY_INFO_LAYOUT
        ];

    (function setCompanyInfoOnChildren(
        containerBlock: ContainerableBlock | CXContainerableBlock,
        struct: ICompositionLayoutStruct
    ) {
        struct.children.forEach((childStruct, index) => {
            const childBlock = containerBlock.childBlocks[index];
            if (childBlock instanceof ImageModel) {
                childBlock.image = (childStruct.image ||
                    childBlock.image) as IImage;
            } else if (childBlock instanceof TextModel) {
                childBlock.content = getTextBlockContent(
                    childStruct,
                    childBlock.flow.isLeadForm
                );
            } else if (childBlock instanceof ConatinerableModel) {
                setCompanyInfoOnChildren(childBlock, childStruct);
            }
        });
    })(block, layoutStruct);
}

export const analyticsBlockType = (blockType, compositionType) => {
    if (blockType === 'container_block') {
        if (compositionType === 'default') {
            return 'composition';
        }

        return compositionType;
    }

    return blockType;
};

export function getErrorMessagesList(
    block: TBlock,
    additionalProps: IErrorAdditionalProps
) {
    const {
        isInvoiceSummaryBlock,
        isQuestionBlock,
        isSignatureBlock,
        isSignaturesBlock,
        isContractHtmlBlock,
        isInvoiceServiceItemBlock,
        isServicesBlock,
        isVideoBlock,
        isRecurringScheduleBlock,
        isScheduleSessionBlock,
        errors
    } = block;
    const errorMessages = [];
    const t = getFixedT(null, NAMESPACES.BUILDER, 'payment.canvas.errors');

    if (isInvoiceSummaryBlock) {
        if (errors.discountSubtotalValue) {
            errorMessages.push('Discount must be valid');
        }
        if (errors.discountRelativeValue) {
            errorMessages.push('Discount % must be 100% or lower');
        }
        if (errors.taxRelativeValue) {
            errorMessages.push('Tax % must be 100% or lower');
        }
    }
    if (isQuestionBlock) {
        if (errors.text) {
            errorMessages.push('Add question text');
        }
        if (errors.option) {
            errorMessages.push('Define all selection options');
        }
    }
    if (isContractHtmlBlock) {
        errorMessages.push(
            'All smart fields on contract must be filled (or set to client must fill or can edit)'
        );
    }
    if (isScheduleSessionBlock) {
        errorMessages.push('Add a session to the scheduler block.');
    }
    if (isSignatureBlock) {
        errorMessages.push(
            'All signatures on contract must be assigned to a team member, client or collaborator'
        );
    }
    if (isSignaturesBlock) {
        errorMessages.push(
            'The contract must contain at least 1 signature assigned to a team member and 1 signature assigned to a client'
        );
    }
    if (isInvoiceServiceItemBlock) {
        errorMessages.push('All items on invoice must have a title');
    }
    if (block instanceof PaymentScheduleBlock) {
        const {
            [PaymentScheduleBlockErrors.PaymentLessThanMinimum]:
                paymentLessThanMinimum,
            [PaymentScheduleBlockErrors.FixedPaymentMoreThanMaximum]:
                fixedMoreThanMaximum,
            [PaymentScheduleBlockErrors.MissingMandatoryItemsForCustomPayment]:
                missingRequiredServices,
            [PaymentScheduleBlockErrors.MissingPercentageBasedPayment]:
                missingPercentageBasedPayment,
            [PaymentScheduleBlockErrors.PayInFullPercentageDiscountValidation]:
                payInFullPercentageDiscountValidation,
            [PaymentScheduleBlockErrors.PayInFullFixedDiscountValidation]:
                payInFullFixedDiscountValidation,
            ...otherErrors
        } = block.blockInputValidations as {
            [key in PaymentScheduleBlockErrors]: boolean | number | undefined;
        };

        const isGeneralPaymentsError =
            Object.values(otherErrors).filter(Boolean).length;

        if (paymentLessThanMinimum) {
            errorMessages.push(
                'Every single payment amount must be higher than $0.5'
            );
        }

        if (missingRequiredServices) {
            errorMessages.push(
                'To set a custom payment amount, add a base or required service selection to the invoice.'
            );
        } else if (!falseNotZero(fixedMoreThanMaximum)) {
            errorMessages.push(
                `The custom payments, in total, cannot exceed ${toCurrency(
                    fixedMoreThanMaximum as number
                )}.`
            );
        }

        if (missingPercentageBasedPayment) {
            errorMessages.push(t('missing_percentage_based_payment'));
        }

        if (payInFullPercentageDiscountValidation) {
            errorMessages.push(t('pay_in_full_percentage_discount_validation'));
        }

        if (payInFullFixedDiscountValidation) {
            errorMessages.push(t('pay_in_full_fixed_discount_validation'));
        }

        if (isGeneralPaymentsError) {
            errorMessages.push(
                'Total amount of all payments on payment schedule must match grand total on invoice'
            );
        }
    }
    if (isRecurringScheduleBlock) {
        if (errors.dateBeforeToday) {
            errorMessages.push(
                'Invoice payment due date has to be today or in the future'
            );
        }
        if (errors.totalLessThanSent) {
            errorMessages.push(
                `Set the number of invoices to ${errors.totalLessThanSent} or more.`
            );
        }
    }
    if (isServicesBlock) {
        errorMessages.push('Invoices should have at least one item/package');
    }
    if (isVideoBlock) {
        errorMessages.push('Invalid video URL');
    }
    if (block instanceof ServiceSelectionItemBlock) {
        const serviceItemBlockErrors = block.getErrors(additionalProps);

        if (serviceItemBlockErrors.itemTitle && !block.isInPackage) {
            errorMessages.push('Service name must be filled');
        }
        if (serviceItemBlockErrors.itemQuantity && !block.isInPackage) {
            errorMessages.push('Invalid amounts for min and max quantity');
        }
        if (
            (serviceItemBlockErrors.itemTitle && block.isInPackage) ||
            serviceItemBlockErrors.subItemTitle
        ) {
            errorMessages.push('Service sub items name must be filled');
        }
        if (
            (serviceItemBlockErrors.itemQuantity && block.isInPackage) ||
            serviceItemBlockErrors.subItemQuantity
        ) {
            errorMessages.push(
                'Service sub items has invalid amounts for min and max quantity'
            );
        }
        if (serviceItemBlockErrors.invoiceTitle) {
            errorMessages.push('All items on invoice must have a title');
        }
        if (serviceItemBlockErrors.emptyDigitalProductLink) {
            errorMessages.push(t('empty_digital_product_link'));
        } else if (serviceItemBlockErrors.invalidDigitalProductLink) {
            errorMessages.push(t('invalid_digital_product_link'));
        }
        if (serviceItemBlockErrors.sessionConfig) {
            errorMessages.push('Service session must be filled');
        }
    }
    return errorMessages;
}

export function sortBlocksByDisplayPrecedence(blockA: TBlock, blockB: TBlock) {
    if (blockA.parent_id === blockB.parent_id) {
        return blockA.order - blockB.order;
    }

    if (blockA.page_id !== blockB.page_id) {
        return blockA.page.order - blockB.page.order;
    }

    const blockARootOrder =
        blockA.calculateRootLevelParent()?.order ?? blockA.order;
    const blockBRootOrder =
        blockB.calculateRootLevelParent()?.order ?? blockB.order;

    return blockARootOrder - blockBRootOrder;
}

export function getAllChildren({
    block,
    blocksMap
}: {
    block: IBaseBlockResponse;
    blocksMap: Record<string, TBlockResponse>;
}) {
    const containerBlock = block as IContainerableBlockResponse;
    if (!containerBlock?.children) {
        return [block?.block_id];
    }

    return [
        block.block_id,
        containerBlock.children.map(childBlock =>
            getAllChildren({ block: blocksMap[childBlock.block_id], blocksMap })
        )
    ];
}

// TODO: remove after fixing HON-20940
export function fixCorruptedRecapVariableData(
    input: string | IPaymentRecap[]
): IPaymentRecap[] {
    // If the input is already an array of IPaymentRecap, return it as-is
    if (Array.isArray(input)) {
        for (const item of input) {
            if ('payment_block_id' in item && 'due_date' in item) {
                return input; // Return as-is if the input array matches the IPaymentRecap format
            }
        }
    }

    // If the input is a string, attempt to transform it
    if (typeof input === 'string') {
        const regex =
            /:payment_block_id=>"([\w\-]+)", :due_date=>"([\w\s\d,]+)"/g;

        const result: IPaymentRecap[] = [];
        let match: RegExpExecArray | null;

        while ((match = regex.exec(input)) !== null) {
            result.push({
                payment_block_id: match[1],
                due_date: match[2] // Convert the date string to a Date object
            });
        }

        return result;
    }

    // If input is neither a string nor an array of IPaymentRecap, return it as-is (though this might be unexpected)
    return input;
}
